import type { ActivityDetail } from './api'

export type ViewerAccess = 'creator' | 'confirmed' | 'invited' | 'restricted'

/**
 * D-043 / API_CONTRACT §6.1: only the creator and confirmed participants get
 * the exact end time, meeting point, description, price and uploaded cover.
 * `pending`/`cancelled`/`removed`/`rejected` callers and anonymous viewers get
 * the masked projection.
 */
export function canViewSensitiveFields(
  activity: Pick<ActivityDetail, 'viewerStatus'>,
): boolean {
  return (
    activity.viewerStatus === 'creator' ||
    activity.viewerStatus === 'joined' ||
    activity.viewerStatus === 'attended'
  )
}

/**
 * The participant list is restricted to creator/confirmed participants or a
 * matching invite token (D-043). Detail echoes `inviteToken` only when the
 * supplied token matched, so compare it as a second line of defence.
 */
export function canViewParticipants(
  activity: Pick<ActivityDetail, 'viewerStatus' | 'inviteToken'>,
  requestedInviteToken: string | null,
): boolean {
  if (canViewSensitiveFields(activity)) return true
  return Boolean(
    requestedInviteToken &&
      activity.inviteToken &&
      requestedInviteToken === activity.inviteToken,
  )
}

export function viewerAccess(
  activity: Pick<ActivityDetail, 'viewerStatus' | 'inviteToken'>,
  requestedInviteToken: string | null,
): ViewerAccess {
  if (activity.viewerStatus === 'creator') return 'creator'
  if (activity.viewerStatus === 'joined' || activity.viewerStatus === 'attended') {
    return 'confirmed'
  }
  if (canViewParticipants(activity, requestedInviteToken)) return 'invited'
  return 'restricted'
}

/**
 * The detail endpoint returns the invite token for creator/confirmed viewers
 * and for a matching `?invite=`. Never display the token; only use it to keep
 * invite-only share links working.
 */
export function shareableInviteToken(
  activity: Pick<ActivityDetail, 'participationMode' | 'inviteToken'>,
  requestedInviteToken: string | null,
): string | null {
  if (activity.participationMode !== 'invite') return null
  if (!activity.inviteToken) return null
  if (requestedInviteToken && requestedInviteToken !== activity.inviteToken) {
    return null
  }
  return activity.inviteToken
}

function timestamp(value: string | null): number | null {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

/** The activity start date has passed (or is unknown/invalid). */
export function activityStarted(
  activity: Pick<ActivityDetail, 'startAt'>,
  now: number = Date.now(),
): boolean {
  const start = timestamp(activity.startAt)
  if (start === null) return false
  return start <= now
}

/**
 * Creator edit/cancel is only allowed before the start while the activity is
 * still editable (API_CONTRACT §6.6/§6.8). The API re-checks this rule; the UI
 * hides or disables the actions to avoid a guaranteed 400.
 */
export function canEditActivity(
  activity: Pick<ActivityDetail, 'viewerStatus' | 'status' | 'startAt'>,
  now: number = Date.now(),
): boolean {
  if (activity.viewerStatus !== 'creator') return false
  if (
    activity.status !== 'draft' &&
    activity.status !== 'published' &&
    activity.status !== 'full'
  ) {
    return false
  }
  return !activityStarted(activity, now)
}

/**
 * Leaving/cancelling a participation row is only possible before the start;
 * the API answers 400 afterwards (API_CONTRACT §6.11).
 */
export function canLeaveActivity(
  activity: Pick<ActivityDetail, 'viewerStatus' | 'startAt'>,
  now: number = Date.now(),
): boolean {
  const status = activity.viewerStatus
  if (
    status !== 'joined' &&
    status !== 'attended' &&
    status !== 'pending' &&
    status !== 'waitlisted'
  ) {
    return false
  }
  return !activityStarted(activity, now)
}

/**
 * D-043: feedback opens one hour after the activity ends and both users must be
 * confirmed participants. The form is only rendered for joined/attended
 * viewers; the API still enforces the rule server-side.
 */
export function feedbackAvailable(
  activity: Pick<ActivityDetail, 'viewerStatus' | 'status' | 'endAt'>,
  now: number = Date.now(),
): boolean {
  if (
    activity.viewerStatus !== 'creator' &&
    activity.viewerStatus !== 'joined' &&
    activity.viewerStatus !== 'attended'
  ) {
    return false
  }
  if (activity.status === 'cancelled' || activity.status === 'draft') {
    return false
  }
  const end = timestamp(activity.endAt)
  if (end === null) return false
  return end + 60 * 60 * 1000 <= now
}
